import { CSSProperties } from 'react';
import { LockKeyhole } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useActiveConversation } from '../../chat/ActiveConversationContext';
import { Button, Card, Typography } from '../ui';
import { BriefHeadline } from '../MaestroBrief/BriefHeadline';
import styles from './BriefSlot.module.css';

// Mobile-card rendering of the "unlock morning meetings" brief state — the flat desktop content
// (`ActivateBrief`) presented as a quiet floating `Card`, matching the native `BriefActivateCard`
// (Figma 8265:32169): a lock eyebrow, the editorial "Start *every day* informed" headline,
// body copy, and a solid-black "Activate now" CTA that opens a chat.

// The editorial headline flips the hero's run styling: the base is the heavy sans and the
// `**…**`-marked run is the serif accent (the hero has it the other way round). `brief.activate.headline`
// carries the markers per locale; here `boldStyle` is the serif so the marked run reads serif.
const HEADLINE_SANS: CSSProperties = {
  fontFamily: 'var(--brand-font-display)',
  fontSize: 32,
  fontWeight: 'var(--sym-weight-semibold)',
  lineHeight: '32px',
  letterSpacing: '-0.96px',
  color: 'rgb(var(--ink-rgb))',
};

const HEADLINE_SERIF: CSSProperties = {
  fontFamily: 'var(--brand-font-serif)',
  fontSize: 32,
  fontWeight: 400,
  lineHeight: '32px',
  letterSpacing: '-0.96px',
  fontFeatureSettings: "'dlig' on",
};

// Body copy — Wix Madefor Text 14/20, -0.42px, black (Figma 8265:32185). Pinned inline because
// `.sym-*` type variants out-specify the module class and carry 18px / -0.02em.
const BODY_TEXT: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 14,
  fontWeight: 'var(--sym-weight-semibold)',
  lineHeight: '20px',
  letterSpacing: '-0.42px',
  color: 'rgb(var(--ink-rgb))',
  fontFeatureSettings: "'dlig' on",
};

export function MobileActivateBrief() {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const { openChatWithMessage } = useActiveConversation();

  return (
    <Card component="section" style={{ textAlign: 'start' }} aria-labelledby="brief-activate-title">
      <p className={styles.eyebrow}>
        <LockKeyhole size={16} aria-hidden />
        <Typography variant="h4" color="strong" component="span">
          {t('brief.activate.eyebrow')}
        </Typography>
      </p>
      <div id="brief-activate-title" className={styles.mobileHeadline} style={HEADLINE_SANS}>
        <BriefHeadline text={t('brief.activate.headline')} boldStyle={HEADLINE_SERIF} />
      </div>
      <p className={styles.mobileBody} style={BODY_TEXT}>
        {t('brief.activate.body')}
      </p>
      <div className={styles.mobileCta}>
        <Button
          onClick={() => {
            logPageClickBi({ page_name: 'home', button_name: 'brief_activate' });
            openChatWithMessage({ kind: 'new', message: t('brief.activate.chatMessage') });
          }}
        >
          {t('brief.activate.cta')}
        </Button>
      </div>
    </Card>
  );
}
